import { NgClass } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, inject, NgZone, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';

import { NgOptionHighlightDirective } from '@ng-select/ng-option-highlight';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { ICellEditorAngularComp } from 'ag-grid-angular';

import { resolveValue$ } from '@lvflanders/bar-ng';

import { BarKeyCode } from '../bar-key-code.enum';
import { IBarSelectCellEditorParams } from './bar-select-cell-editor-params';

/** AG grid editor for selecting items in a dropdown through ng-select. */
@Component({
    selector: 'bar-select-editor',
    templateUrl: './bar-select-cell-editor.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgSelectModule, FormsModule, ReactiveFormsModule, NgClass, MatTooltipModule, NgOptionHighlightDirective]
})
export class BarSelectCellEditor implements ICellEditorAngularComp, AfterViewInit {
    readonly #zone = inject(NgZone);
    readonly #destroyRef = inject(DestroyRef);

    readonly input = viewChild(NgSelectComponent);

    protected params: IBarSelectCellEditorParams;
    protected formControl: FormControl;
    readonly items = signal<any[]>(undefined);

    agInit(params: IBarSelectCellEditorParams): void {
        this.params = params;
        this.formControl = params.formControl || new FormControl(params.value);

        const items$ = resolveValue$((this.params as any).items);
        items$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((items) => {
            this.items.set(items);
        });
    }

    ngAfterViewInit(): void {
        // put timeout because it might be that we get previous focussed cell
        setTimeout(() => {
            // automatically set the focus when the column gets in edit modus (the editor is then initialized)
            const focusedCell = this.params.api.getFocusedCell();
            // only perform this if this is the column having the focus (fullRowEdit modus)
            if (focusedCell?.column == this.params.column) {
                // NOTE the timeout is needed to have effect
                this.input().focus();
                // put carret in the ng-select
                this.input().filter('');
                // close the popup which opens when filtering
                this.input().close();

                // when the editor gets the focus (in read modus) and we type a character we want to start filtering already
                if (this.#checkAlfaNumeric(this.params.eventKey)) {
                    // NOTE use request animation frame to be sure the input is ready (because we close it again)
                    // otherwised causing "TypeError: Cannot read properties of undefined (reading 'clientHeight')" in ng-select in combination with virtualScroll enabled
                    requestAnimationFrame(() => this.input().filter(this.params.eventKey));
                }
            }
        });

        if (this.params.exitEditOnSelect) {
            this.input().openEvent.subscribe(() => {
                if (this.input().isOpen) {
                    // NOTE the timeout is needed to have effect
                    window.setTimeout(() => {
                        // NOTE double check because there is a timeout and it might already be closed
                        if (this.input().isOpen && this.input().dropdownPanel()) {
                            this.input()
                                .dropdownPanel()
                                .contentElementRef()
                                .nativeElement.addEventListener('click', this.#onHandleDropDownPanelClick.bind(this));
                        }
                    });
                }
            });
        }
    }

    getValue(): any {
        return this.formControl.value;
    }

    isPopup(): boolean {
        // we don't want the component to appear in a popup
        return false;
    }

    onKeyDown(event: KeyboardEvent): void {
        const key = event.which || event.keyCode;

        switch (key) {
            case BarKeyCode.Enter: {
                const focusedCell = this.params.api.getFocusedCell();
                if (this.params.api.getGridOption('enterNavigatesVerticallyAfterEdit')) {
                    // NOTE we are suppressing the enter behavior on the col def because otherwise the ng-select never gets the enter to select a value
                    // so here we must keep the default behavior in case the enter key would not have been suppressed
                    // calculate the next row index
                    const nextRowIndex = focusedCell.rowIndex + 1;
                    if (nextRowIndex < this.params.api.paginationGetRowCount()) {
                        // set the focus to the next cell
                        this.params.api.setFocusedCell(nextRowIndex, focusedCell.column);
                    } else {
                        // no next row just stop editing
                        this.params.api.stopEditing();
                    }
                } else {
                    //  normal behavior stop editing (enter doesn't move down after edit), but keep focus
                    this.params.api.stopEditing();
                    this.params.api.setFocusedCell(focusedCell.rowIndex, focusedCell.column);
                }
                break;
            }
            case BarKeyCode.Delete: {
                // in geval van delete key en nog niets ingetyped dan veld leegmaken
                if (this.params.clearValueOnDelete && !this.input().searchTerm) this.formControl.setValue(undefined);
                break;
            }
            case BarKeyCode.Backspace: {
                // in geval van backspace key en 1 karakter ingevuld  of nog niets ingetyped dan veld leegmaken
                if (this.params.clearValueOnBackspace && (!this.input().searchTerm || this.input().searchTerm.length === 1))
                    this.formControl.setValue(undefined);
                break;
            }
            case BarKeyCode.UpArrow: {
                this.#arrowSelection(event, true);
                break;
            }
            case BarKeyCode.DownArrow: {
                this.#arrowSelection(event, false);
                break;
            }
        }
    }

    protected resolveValueOptionParam(param: any | ((val: any) => any), item: any): any {
        if (typeof param === 'function') {
            return (param as (val: any) => any)(item);
        } else {
            return param;
        }
    }

    /**
     * Performs the logic on arrow down and arrow up.
     * @param event The original keyboard event.
     * @param up Whether going up or down.
     */
    #arrowSelection(event: KeyboardEvent, up: boolean): void {
        // in case the input is open and arrow selection is enabled we need to handle it
        if (this.input().isOpen && this.params.enableArrowSelection) {
            // handle it stop propagating
            event.stopPropagation();
            // check if we have something in the popup
            if (this.input().itemsList.filteredItems.length > 0) {
                let selectedIndex;
                if (this.input().selectedItems[0]) {
                    // define the selected index in the list
                    selectedIndex = this.input().itemsList.filteredItems.findIndex((item) => {
                        return (item.value as any)[this.params.bindValue] === (this.input().selectedItems[0].value as any)[this.params.bindValue];
                    });
                } else {
                    // nothing chosen yet
                    selectedIndex = -1;
                }

                if (up) {
                    // we go up meaning we need to go higher up the list
                    selectedIndex = selectedIndex <= 0 ? this.input().itemsList.filteredItems.length - 1 : selectedIndex - 1;
                    // make the selection
                    this.#makeSelection(selectedIndex);
                } else {
                    // we go down meaning we need to go lower in the list
                    selectedIndex = selectedIndex === this.input().itemsList.filteredItems.length - 1 ? 0 : selectedIndex + 1;
                    // make the selection
                    this.#makeSelection(selectedIndex);
                }
            }
        }
    }

    /**
     * Checks whether a given value is alfanumeric.
     * @param charStr The string.
     */
    #checkAlfaNumeric(charStr: string): boolean {
        if (charStr && charStr.length > 1) return false;
        return charStr ? !!/^[a-zA-Z0-9]*$/.test(charStr) : false;
    }

    /**
     * Handles the click in the dropdown panel.
     * @param event
     */
    #onHandleDropDownPanelClick(event): void {
        // run in Angular zone
        this.#zone.run(() => {
            // get the focussed cell
            const focusedCell = this.params.api.getFocusedCell();
            // stop editing
            this.params.api.stopEditing();
            // set focussed cell again
            this.params.api.setFocusedCell(focusedCell.rowIndex, focusedCell.column);
        });
    }

    /** Make the selection based on the selected index. */
    #makeSelection(index: number): void {
        // check if we have something to select
        if (index > -1) {
            // set the selected value (model binding)
            this.formControl.setValue((this.input().itemsList.filteredItems[index].value as any)[this.params.bindValue]);
            // scroll to the selected index
            if (this.input().dropdownPanel) this.input().dropdownPanel().scrollTo(this.input().itemsList.filteredItems[index]);
            this.input().itemsList.unmarkItem();
        }
        // use a timeout to focus the input
        window.setTimeout(() => {
            this.input().focus();
        }, 0);
    }
}
